import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { roleHome } from "@/lib/roles";
import { aspectSize } from "@/lib/img";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Load the Google Identity Services script once.
function useGoogleScript() {
  const [ready, setReady] = useState(!!(window.google && window.google.accounts));
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || ready) return;
    if (document.getElementById("gsi-script")) return;
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true; s.defer = true; s.id = "gsi-script";
    s.onload = () => setReady(true);
    document.head.appendChild(s);
  }, [ready]);
  return ready;
}

export default function Auth({ mode = "login" }) {
  const { user, requestLink, googleLogin } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const isReg = mode === "register";
  // Sign-in authenticates existing accounts only; only /register may create one.
  const intent = isReg ? "register" : "login";

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("seller");
  const [businessName] = useState("");
  const [country] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [noAccount, setNoAccount] = useState(false); // login hit an unknown email
  const [sent, setSent] = useState(null); // { message, dev_link }

  // Carry any ?next= through to registration so the user lands where they intended.
  const registerHref = sp.get("next") ? `/register?next=${encodeURIComponent(sp.get("next"))}` : "/register";

  const gReady = useGoogleScript();
  const gBtn = useRef(null);

  const goAfterAuth = useCallback((user) => {
    const next = sp.get("next");
    nav(next || roleHome(user));
  }, [nav, sp]);

  const onGoogle = useCallback(async (resp) => {
    setBusy(true); setErr(""); setNoAccount(false);
    try {
      const user = await googleLogin(resp.credential, { role, business_name: businessName, country, intent });
      toast.success("Welcome to GetJewelDesigns.");
      goAfterAuth(user);
    } catch (ex) {
      // On the sign-in page an unknown email is a "no account yet" case, not an error.
      if (!isReg && ex?.response?.status === 404) setNoAccount(true);
      else setErr(apiErr(ex));
    } finally { setBusy(false); }
  }, [googleLogin, role, businessName, country, goAfterAuth, intent, isReg]);

  // Render the Google button once the script is ready.
  useEffect(() => {
    if (!gReady || !GOOGLE_CLIENT_ID || !gBtn.current) return;
    try {
      window.google.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID, callback: onGoogle });
      // GSI only accepts a pixel width — cap to the container so the iframe
      // doesn't overflow small phones (320–375px viewports).
      const w = Math.min(360, gBtn.current.parentElement?.clientWidth || 360);
      window.google.accounts.id.renderButton(gBtn.current, { theme: "outline", size: "large", width: w, text: isReg ? "signup_with" : "continue_with" });
    } catch (e) { /* ignore */ }
  }, [gReady, onGoogle, isReg]);

  const sendLink = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true); setErr("");
    try {
      const res = await requestLink({ email: email.trim(), intent, name: name.trim() || undefined, role, business_name: businessName || undefined, country: country || undefined });
      setSent(res);
      toast.success("Sign-in link sent.");
    } catch (ex) { setErr(apiErr(ex)); } finally { setBusy(false); }
  };

  // Already signed in — there is nothing to do here. Only the resolved user is
  // trusted (never the cached hint): a dead session lands here via
  // ProtectedRoute's /login?next=…, and redirecting optimistically would bounce
  // the user straight back and loop.
  if (user) return <Navigate to={sp.get("next") || roleHome(user)} replace />;

  return (
    <div className="min-h-[80vh] grid grid-cols-1 md:grid-cols-2">
      <Seo title={isReg ? "Create an account" : "Sign in"} noindex />
      <div className="hidden md:block border-r hairline">
        <img src="https://images.pexels.com/photos/11504786/pexels-photo-11504786.jpeg" {...aspectSize("1/1", 1200)} alt="jewellery" className="w-full h-full object-cover opacity-70" />
      </div>
      <div className="flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md space-y-6" data-testid="auth-form">
          <div className="mb-2">
            <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">{isReg ? "Create account" : "Sign in"}</div>
            <h1 className="font-serif text-4xl">{isReg ? "Begin your project." : "Welcome back."}</h1>
            <p className="text-sm text-muted-foreground mt-3">Passwordless — continue with Google or a one-time email link.</p>
          </div>

          {sent ? (
            <div className="border hairline p-6 space-y-3" data-testid="auth-link-sent">
              <div className="flex items-center gap-3 text-primary"><Mail className="h-5 w-5" strokeWidth={1} /><span className="font-serif text-2xl">Check your email</span></div>
              <p className="text-sm text-muted-foreground">{sent.message || `We sent a sign-in link to ${email}. It expires shortly.`}</p>
              {sent.dev_link && (
                <a href={sent.dev_link} data-testid="auth-dev-link" className="block text-xs text-primary underline underline-offset-4 break-all">
                  Dev link (preview only): {sent.dev_link}
                </a>
              )}
              <button onClick={() => setSent(null)} className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground">Use a different email</button>
            </div>
          ) : (
            <>
              {isReg && (
                <div>
                  <Label className="text-xs uppercase tracking-widest">I am a…</Label>
                  <div className="mt-2 grid grid-cols-2 gap-3">
                    {["seller", "freelancer"].map((r) => (
                      <button key={r} type="button" data-testid={`role-${r}`} onClick={() => setRole(r)}
                        className={`border px-4 py-3 text-xs uppercase tracking-widest transition-colors duration-300 ${role === r ? "border-primary text-primary bg-primary/10" : "hairline text-muted-foreground"}`}>
                        {r === "seller" ? "Client" : "Designer"}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {noAccount && (
                <div className="border border-primary/40 bg-primary/10 p-4 space-y-3" data-testid="auth-no-account">
                  <div className="font-serif text-lg">No account found</div>
                  <p className="text-sm text-muted-foreground">
                    We couldn't find an account for that email. Create one to choose whether you're a Client or a Designer.
                  </p>
                  <Link to={registerHref} data-testid="auth-no-account-register" className="inline-flex items-center gap-2 border border-primary text-primary px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors duration-300">
                    Create an account <ArrowRight className="h-4 w-4" strokeWidth={1} />
                  </Link>
                </div>
              )}

              {GOOGLE_CLIENT_ID && (
                <div className="space-y-3">
                  <div ref={gBtn} data-testid="google-btn" className="flex justify-center" />
                  <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-muted-foreground">
                    <span className="h-px flex-1 bg-foreground/10" /> or <span className="h-px flex-1 bg-foreground/10" />
                  </div>
                </div>
              )}

              <form onSubmit={sendLink} className="space-y-4">
                {isReg && (
                  <div>
                    <Label htmlFor="auth-name" className="text-xs uppercase tracking-widest">Name</Label>
                    <Input id="auth-name" data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" className="mt-2 inp" placeholder="Your name" />
                  </div>
                )}
                <div>
                  <Label htmlFor="auth-email" className="text-xs uppercase tracking-widest">Email</Label>
                  <Input id="auth-email" data-testid="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" placeholder="you@example.com" className="mt-2 inp" />
                </div>
                {err && <div className="text-sm text-red-300 border border-red-500/40 bg-red-500/10 p-3">{err}</div>}
                <Button data-testid="auth-submit" type="submit" disabled={busy} className="w-full rounded-none bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs py-6">
                  {busy ? "Please wait…" : (<span className="inline-flex items-center gap-2">Email me a sign-in link <ArrowRight className="h-4 w-4" strokeWidth={1} /></span>)}
                </Button>
              </form>

              <div className="text-sm text-muted-foreground">
                {isReg ? (
                  <>Already have an account? <Link to="/login" data-testid="auth-switch-login" className="text-primary underline underline-offset-4">Sign in</Link></>
                ) : (
                  <>New here? <Link to="/register" data-testid="auth-switch-register" className="text-primary underline underline-offset-4">Create an account</Link></>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
